import React, { useRef, useState, useEffect } from 'react';
import * as api from '../api';
import Transcript from '../components/Transcript';
import AudioPlayer from '../components/AudioPlayer';

const CAMPAIGN_RE = /--\d+--/;

function inferTypeFromName(name = "") {
  return CAMPAIGN_RE.test(name) ? "Кампания" : "Онлайн заявка";
}

export default function Dashboard({ onAfterUpload }) {
  const [file, setFile] = useState(null);
  const [fileType, setFileType] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadInfo, setLoadInfo] = useState({ text: '', pct: 0 });
  const [result, setResult] = useState(null);
  const [summLoading, setSummLoading] = useState(false);
  const fileInputRef = useRef(null);
  const dzRef = useRef(null);
  const prevUrlRef = useRef(null);

  const openPicker = () => { if (!loading) fileInputRef.current?.click(); };
  const handleZoneClick = (e) => {
    if (loading) return;
    if (e.target.closest('button, a, input, label, textarea, select')) return;
    openPicker();
  };
  const handleZoneKeyDown = (e) => {
    if (loading) return;
    if (!(e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar')) return;
    if (e.target !== e.currentTarget) return;
    e.preventDefault(); openPicker();
  };

  useEffect(() => {
    const dz = dzRef.current; if (!dz) return;
    const onOver = e => { e.preventDefault(); dz.classList.add('bg-slate-900/50'); };
    const onLeave = () => dz.classList.remove('bg-slate-900/50');
    const onDrop = e => {
      e.preventDefault(); dz.classList.remove('bg-slate-900/50');
      const files = [...e.dataTransfer.files];
      const mp3 = files.find(f => /\.mp3$/i.test(f.name) || f.type === 'audio/mpeg');
      if (mp3) {
        setFile(mp3);
        setFileType(inferTypeFromName(mp3.name));
      } else {
        alert('Моля, качете MP3 файл.');
      }
    };
    dz.addEventListener('dragover', onOver);
    dz.addEventListener('dragleave', onLeave);
    dz.addEventListener('drop', onDrop);
    return () => {
      dz.removeEventListener('dragover', onOver);
      dz.removeEventListener('dragleave', onLeave);
      dz.removeEventListener('drop', onDrop);
    };
  }, []);

  const doUpload = async () => {
    if (!file) return;

    let t1, t2, t3;
    const clearPhaseTimers = () => {
      if (t1) clearTimeout(t1);
      if (t2) clearTimeout(t2);
      if (t3) clearTimeout(t3);
    };

    setLoading(true);
    setLoadInfo({ text: 'Качване и транскрибиране…', pct: 20 });

    t1 = setTimeout(() => setLoadInfo(li => ({ ...li, pct: 55 })), 800);

    t2 = setTimeout(() => setLoadInfo({ text: 'Автоматична оценка…', pct: 80 }), 2000);

    try {
      const data = await api.upload(file, fileType);

      setLoadInfo({ text: 'Финализиране…', pct: 95 });

      if (data?.transcript) {
        const localUrl = URL.createObjectURL(file);
        if (prevUrlRef.current) URL.revokeObjectURL(prevUrlRef.current);
        prevUrlRef.current = localUrl;

        setResult({
          id: data.transcript_id,
          filename: file.name,
          transcript: data.transcript,
          audioUrl: localUrl,
          segments: [],
        });

        try {
          const t = await api.transcript(data.transcript_id); // { transcript, segments }
          if (Array.isArray(t.segments)) {
            setResult(prev => prev ? { ...prev, segments: t.segments } : prev);
          }
        } catch (e) {
          console.warn('No segments yet:', e);
        }

        onAfterUpload?.();

        setLoadInfo({ text: 'Готово', pct: 100 });
      } else {
        alert('Transcription failed');
      }
    } catch (e) {
      console.error(e);
      alert('Грешка при транскрибиране');
    } finally {
      clearPhaseTimers();
      setTimeout(() => setLoading(false), 250);
    }
  };


  useEffect(() => {
    return () => {
      if (prevUrlRef.current) URL.revokeObjectURL(prevUrlRef.current);
    };
  }, []);


  const canUpload = !!file && !loading;
  const uploadBtnClass = canUpload ? 'btn btn-primary hover:scale-[1.02] transition-transform' : 'btn btn-ghost opacity-60 cursor-not-allowed';

  return (
    <section className="space-y-6">
      <div className="card rounded-2xl p-6">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-bold">Транскрибиране</h2>
          <div className="text-sm text-slate-400">Поддържано: .mp3</div>
        </div>

        <input
          ref={fileInputRef}
          type="file"
          accept="audio/mpeg,.mp3"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0] || null;
            setFile(f);
            setFileType(f ? inferTypeFromName(f.name) : '');
          }}
        />

        <div
          ref={dzRef}
          onClick={handleZoneClick}
          onKeyDown={handleZoneKeyDown}
          role="button"
          tabIndex={0}
          className="mt-4 border-2 border-dashed border-slate-700 rounded-2xl p-8 grid place-items-center text-center transition-colors cursor-pointer"
        >
          <div>
            <div className="mx-auto w-14 h-14 rounded-2xl grid place-items-center bg-slate-800/70 border border-slate-700">
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#a78bfa" strokeWidth="2">
                <path d="M12 5v14" /><path d="M5 12l7-7 7 7" />
              </svg>
            </div>
            <p className="mt-3 text-slate-300 font-semibold">Провлачете .mp3 или кликнете тук</p>
            <p className="text-sm text-slate-500">Аудиото ще бъде качено за транскрибиране (поддържаме само MP3).</p>

            {file && (
              <div className="mt-3 space-y-2">
                <div className="text-sm text-slate-400">
                  Избран: <span className="font-semibold text-slate-200">{file.name}</span>
                </div>
                <div className="inline-flex items-center gap-2">
                  <select
                    className="bg-[#121825] border border-[#1f2638] rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-violet-600/40"
                    value={fileType}
                    onChange={(e) => setFileType(e.target.value)}
                    title="Изберете тип на файла"
                  >
                    <option value="">Изберете тип</option>
                    <option value="Онлайн заявка">Онлайн заявка</option>
                    <option value="Кампания">Кампания</option>
                  </select>
                </div>
              </div>
            )}

            <div className="mt-4">
              <button
                type="button"
                className={uploadBtnClass}
                onClick={(e) => { e.stopPropagation(); if (canUpload) doUpload(); }}
                disabled={!canUpload} aria-disabled={!canUpload}
                title={!file ? 'Качете MP3 файл' : loading ? 'Обработва се…' : 'Готово за качване'}
              >
                {loading ? 'Transcribing…' : 'Upload & Transcribe'}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="card rounded-2xl p-0 overflow-hidden">
        <div className="px-6 pt-6">
          <h2 className="text-lg font-bold">Активни задачи</h2>
        </div>

        <div className="divide-y divide-slate-800/80" id="jobs">
          {!loading && !result && <div className="p-6 text-slate-400">Няма активни задачи.</div>}

          {loading && (
            <div className="p-6">
              <div className="font-semibold text-slate-100">{file?.name || 'Файл'}</div>
              <div className="text-sm text-slate-500">{loadInfo.text || 'Обработка…'}</div>
              <div className="mt-4 progress">
                <span
                  className="transition-[width] duration-500 ease-out"
                  style={{ width: `${Math.max(5, Math.min(100, loadInfo.pct || 0))}%` }}
                />
              </div>
            </div>
          )}

          {result && !loading && (
            <div className="p-6">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="font-semibold text-slate-100">{result.filename}</div>
                  <div className="text-sm text-slate-500">Готово</div>
                </div>
              </div>

              {result.audioUrl && (
                <div className="mt-4">
                  <AudioPlayer src={result.audioUrl} />
                </div>
              )}

              <div className="mt-4">
                <div className="max-h-[420px] overflow-y-auto rounded-xl border border-[#1f2638] bg-[#0e1320]/40 p-3 nice-scrollbar">
                  <Transcript text={result.transcript} segments={result.segments} />
                </div>
              </div>

              <div className="mt-4 flex flex-wrap items-center gap-2">
                <button className="btn btn-ghost" onClick={() => api.downloadFile(result.id)}>Свали</button>
              </div>

            </div>
          )}
        </div>
      </div>
    </section>
  );
}
